import * as Sentry from '@sentry/vue';
import { isIgnorableNetworkError } from '~~/types/NetworkError';
import { isBotUserAgent } from '~/utils/isBotUserAgent';
import { useSupabaseUser } from '~/composables/useSupabaseUser';

export default defineNuxtPlugin({
	name: 'sentry-client',
	setup: (nuxtApp) => {
		const router = useRouter();
		const { public: { sentry, gitCommitRef } } = useRuntimeConfig();

		if (!sentry.dsn) {
			return;
		}

		if (navigator.webdriver || isBotUserAgent(navigator.userAgent)) {
			return;
		}

		const client = Sentry.init({
			app: nuxtApp.vueApp,
			dsn: sentry.dsn,
			environment: sentry.environment,
			release: gitCommitRef,
			integrations: [
				Sentry.browserTracingIntegration({
					router,
					// The defaults (15 s / 30 s) end the pageload before a hanging request.
					childSpanTimeout: 60_000,
					finalTimeout: 60_000,
				}),
				Sentry.replayIntegration(),
				Sentry.vueIntegration({
					tracingOptions: {
						trackComponents: true,
						timeout: 1000,
						hooks: ['mount', 'update', 'unmount'],
					},
				}),
				Sentry.httpClientIntegration({
					failedRequestTargets: [`${window.location.origin}/api/`],
				}),
			],
			sendDefaultPii: true,
			tracesSampleRate: 0.3,
			tracePropagationTargets: ['localhost', 'local.clickfire.io', 'app.clickfire.io'],
			replaysSessionSampleRate: 0.1,
			replaysOnErrorSampleRate: 0.9,
			normalizeDepth: 6,
			enableLogs: true,
			ignoreErrors: [
				/.*\/_nuxt\/builds\/meta\/.*404.*/,
			],
			beforeSend(event, hint) {
				if (isIgnorableNetworkError(hint?.originalException)) {
					return null;
				}

				// These carry no usable stacktrace, so group them by route and status instead.
				if (event.exception?.values?.[0]?.mechanism?.type?.startsWith('auto.http.client')) {
					event.fingerprint = [
						'http-client',
						event.request?.method ?? 'unknown',
						event.request?.url ? new URL(event.request.url).pathname : 'unknown',
						String(event.contexts?.response?.status_code ?? 'unknown'),
					];
				}

				return event;
			},
		});

		const user = useSupabaseUser();

		watch(user, (_user) => {
			Sentry.setUser(_user);
		}, { immediate: true });

		return {
			provide: {
				sentry: client,
			},
		};
	},
});
